import { usePriorityOptions } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { LocationMap } from "@shared/components/LocationMap";
import { PageHeader } from "@shared/components/PageHeader";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Switch } from "@shared/ui/switch";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateIncident,
  useIncidentDetail,
  useIncidentTypeOptions,
  useUpdateIncident,
} from "../../hooks";
import { AssetSearchTable } from "./assetTable";

export const AddIncident = () => {
  const navigate = useNavigate();
  const { action, id } = useParams();
  const [showAssetSection, setShowAssetSection] = useState(false);
  const [linkedAsset, setLinkedAsset] = useState<any | null>(null);

  const [locationData, setLocationData] = useState<any>({
    latitude: 0,
    longitude: 0,
    address: "",
  });

  const { data: priorityList } = usePriorityOptions();
  const { data: incidentTypeList } = useIncidentTypeOptions();
  const { mutateAsync: createIncident, isPending: isCreating } =
    useCreateIncident();
  const { mutateAsync: updateIncident, isPending: isUpdating } =
    useUpdateIncident(id);

  const { data: incidentData } = useIncidentDetail(id);

  const handleLocationChange = (lat: number, lng: number) => {
    form.setValue("latitude", lat);
    form.setValue("longitude", lng);

    setLocationData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  useEffect(() => {
    if (action === "edit" && incidentData) {
      console.log("incidentData", incidentData);
      const incidentFormData = {
        customer_name: incidentData.customerName,
        customer_contact_no: incidentData.customerContactNo,
        customer_email: incidentData.customerEmail,
        incident_type: incidentData.incidentType,
        priority: incidentData.priority,
        incident_arise_date: incidentData.incidentAriseDate,
        description: incidentData.description,
      };
      setLocationData({
        latitude: incidentData?.latitude,
        longitude: incidentData?.longitude,
        address: incidentData?.location,
      });
      handleAssetSelect(incidentData.asset);
      setShowAssetSection(true);
      try {
        FormService.populateForm(form, incidentFormData, formFields);
      } catch (error) {
        toast({
          title: "Failed to populate form",
          variant: "destructive",
        });
      }
    }
  }, [action, id, incidentData]);

  const handleLatitudeChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -90 || numValue > 90) {
      toast({
        title: "Invalid Latitude",
        description: "Latitude must be between -90 and 90 degrees.",
        variant: "destructive",
      });
      return;
    }
    form.setValue("latitude", numValue);

    setLocationData((prev) => ({
      ...prev,
      latitude: numValue,
    }));
  };

  const handleLongitudeChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -180 || numValue > 180) {
      toast({
        title: "Invalid Longitude",
        description: "Longitude must be between -180 and 180 degrees.",
        variant: "destructive",
      });
      return;
    }
    form.setValue("longitude", numValue);

    setLocationData((prev) => ({
      ...prev,
      longitude: numValue,
    }));
  };

  const priorityOptions = useMemo(() => {
    return (
      priorityList?.map((assetType: any) => ({
        value: assetType.key,
        label: assetType.value,
      })) || []
    );
  }, [priorityList]);

  const incidentTypeOptions = useMemo(() => {
    return (
      incidentTypeList?.map((assetType: any) => ({
        value: assetType.key,
        label: assetType.value,
      })) || []
    );
  }, [incidentTypeList]);

  const handleAddressChange = (address: string) => {
    form.setValue("location", address);

    setLocationData((prev) => ({
      ...prev,
      address: address,
    }));
  };

  const formFields: FormField[] = [
    {
      name: "customer_name",
      type: "text",
      label: "Reported By (Full Name)",
      group: "Customer Information",
      required: true,
      placeholder: "Enter full name",
      groupColorNeeded: false,
      classes: FIELD_CLASSES,
      showHelperTooltip: true,
      tooltipText:
        "Full name of the person reporting the incident. Used for follow-up communication and record-keeping. If reported by the field team, enter the team or staff name.",
    },
    {
      name: "customer_contact_no",
      type: "text",
      label: "Customer Contact Number",
      group: "Customer Information",
      required: true,
      placeholder: "(555) 123-4567",
      classes: FIELD_CLASSES,
      showHelperTooltip: true,
      tooltipText:
        "Primary phone number of the reporter. This is mandatory for callback or field team confirmation. Format: (XXX) XXX-XXXX. Ensure accuracy to avoid delays.",
    },
    {
      name: "customer_email",
      type: "email",
      label: "Customer Email (Optional)",
      group: "Customer Information",
      required: false,
      placeholder: "customer@email.com",
      classes: FIELD_CLASSES,
      showHelperTooltip: true,
      tooltipText:
        "Email address of the reporter. Optional, but helpful for sending updates and resolution notifications.",
    },

    {
      name: "incident_type",
      type: "select",
      label: "Incident Type",
      group: "Incident Details",
      required: true,
      placeholder: "Select incident type",
      groupColorNeeded: false,
      options: incidentTypeOptions,
      classes: FIELD_CLASSES,
      showHelperTooltip: true,
      tooltipText:
        "Select the type of issue being reported (e.g., Leak, No Supply, Quality Complaint). The type determines routing rules, SLA targets, and reporting categories.",
    },
    {
      name: "priority",
      type: "select",
      label: "Priority",
      group: "Incident Details",
      required: true,
      placeholder: "Select priority",
      options: priorityOptions,
      classes: FIELD_CLASSES,
      showHelperTooltip: true,
      tooltipText: (
        <ul>
          <li>Select urgency of the incident:</li>
          <li>
            <strong>High:</strong> Immediate service-affecting issues.
          </li>
          <li>
            <strong>Medium:</strong> Same-day issues (e.g., quality concerns).
          </li>
          <li>
            <strong>Low:</strong> Non-urgent, minor issues.
          </li>
          <li>Priority impacts assignment speed and escalation triggers.</li>
        </ul>
      ),
    },
    {
      name: "incident_arise_date",
      type: "datepicker",
      label: "Date Reported",
      group: "Incident Details",
      required: true,
      classes: FIELD_CLASSES,
      defaultValue: format(new Date(), "yyyy-MM-dd"), // Today's date as default
      showHelperTooltip: true,
      allowPast: true,
      tooltipText:
        "The exact date the issue was first reported. Default is current date/time, but you may backdate if the customer delayed reporting. Cannot be set to a future date.",
      allowFuture: false,
    },
    {
      name: "description",
      type: "textarea",
      label: "Customer's Description",
      group: "Incident Details",
      required: true,
      placeholder: "Describe the incident in detail...",
      maxLength: 500,
      minLength: 10,
      rows: 4,
      classes: FIELD_CLASSES,
      showHelperTooltip: true,
      tooltipText:
        "Detailed explanation of the issue from the reporter. Include symptoms, when it started, and any observations. Avoid technical terms unless provided by field staff.",
    },
    {
      name: "location",
      label: "Location",
      type: "text",
      placeholder: "Enter physical location",
      classes: FIELD_CLASSES,
      rows: 4,
      className: "col-span-2 mt-3",
      group: "Location Details",
      groupColorNeeded: false,
      customRender: () => (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Latitude
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., 40.7128"
                value={locationData.latitude || ""}
                onChange={(e) => handleLatitudeChange(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Longitude
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., -74.0060"
                value={locationData.longitude || ""}
                onChange={(e) => handleLongitudeChange(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Address
              </label>
              <Input
                type="text"
                placeholder="Address will appear here"
                value={locationData.address || ""}
                className="w-full bg-gray-50 "
                onChange={(e) => handleAddressChange(e.target.value)}
              />
            </div>
          </div>

          <LocationMap
            latitude={locationData.latitude}
            longitude={locationData.longitude}
            onLocationChange={handleLocationChange}
            onAddressChange={handleAddressChange}
          />
        </div>
      ),
      showHelperTooltip: true,
      tooltipText: (
        <ul>
          <li>Enter Address Manually:</li>
          <li>
            <strong>Enter Address Manually:</strong> Type the street, city, and
            details..
          </li>
          <li>
            <strong>Select on Map:</strong> Use map tool for pinpoint
            accuracy.Always confirm the address to avoid dispatch errors.
          </li>
          <li>
            <strong>Low:</strong> Non-urgent, minor issues.
          </li>
          <li>Priority impacts assignment speed and escalation triggers.</li>
        </ul>
      ),
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleAssetSelect = (asset: any) => {
    form.setValue("asset", asset?.id);
    setLinkedAsset(asset);
  };

  const handleSubmit = async () => {
    const formData = form.getValues();
    const isValid = await FormService.validateForm(form);
    if (!isValid) {
      return;
    }

    if (action === "edit") {
      updateIncident(formData);
      navigate("/ax/field-alerts/incidents");
    } else {
      createIncident(formData);
      navigate("/ax/field-alerts/incidents");
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "locationMethod") {
      if (value === "manual") {
        form.setValue("latitude", 0);
        form.setValue("longitude", 0);
        setLocationData((prev) => ({
          ...prev,
          latitude: 0,
          longitude: 0,
        }));
      }
      if (value === "map") {
        form.setValue("location", "");
        form.setValue("city", "");
        setLocationData((prev) => ({
          ...prev,
          address: "",
        }));
      }
    }
  };

  const handleReset = () => {
    form.reset();
    setLocationData({
      latitude: 0,
      longitude: 0,
      address: "",
    });
  };

  const onAssetSelect = (asset: any) => {
    handleAssetSelect(asset);
  };

  return (
    <div className=" space-y-6">
      <div className="flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/ax/field-alerts/incidents")}
        />
        <PageHeader
          title="Add New Incident"
          subtitle="Report a new incident and capture all relevant details"
        />
      </div>

      <div>
        <DynamicForm
          fields={formFields}
          form={form}
          onFieldChange={handleFieldChange}
          gridColumns={2}
          config={{
            showSubmitButton: false,
            showResetButton: false,
          }}
        />

        <Card>
          <CardHeader>
            <CardTitle>Link Asset (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="linkAsset"
                checked={showAssetSection}
                onCheckedChange={setShowAssetSection}
              />
              <label
                htmlFor="linkAsset"
                className="text-sm font-medium text-gray-700"
              >
                Link a specific asset to this incident
              </label>
            </div>

            {showAssetSection && (
              <div className="space-y-4 border-t pt-4">
                <p className="text-sm text-gray-600">
                  Optional: If the affected asset is already known, find and
                  link it below.
                </p>

                <AssetSearchTable
                  onAssetSelect={onAssetSelect}
                  searchLocation={locationData}
                  linkedAsset={linkedAsset}
                  setLinkedAsset={setLinkedAsset}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/ax/field-alerts/incidents")}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              handleSubmit();
            }}
          >
            {action == "edit" ? "Update Incident" : "Create Incident"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddIncident;
